// dependencies
import React, { useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import "usehooks-ts";
import { z } from "zod";

import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// components
import { AdminTitle } from "../../../../../components/Admin";
import { getRandomTopShelfColor } from "../../../../../components/ColorPicker";
import PageTitle from "../../../../../components/PageTitle";
import Spacer from "../../../../../components/Spacer";
import ComponentLoader from "../../../../../components/Spinner/ComponentLoader";
import Text from "../../../../../components/Text";
import Tooltip from "../../../../../components/Tooltip";

import { useCohort } from "../../../../../api/hooks";
// hooks
import useForm, { ValidationResult } from "../../../../../hooks/useForm";

// api
import { createUserSelectionFromCohort, fetchNewSelectionObject } from "../../../../../api/services";

import { MAX_COHORT_NAME_LENGTH } from "../EngagementSetupConstants";
// styles
import css from "./Cohorts.module.scss";
import DynamicEmployeesCreate from "./Forms/DynamicEmployees";
// Sub Forms
import SetupForm from "./Forms/Setup";
import StaticCohortEmployees from "./Forms/StaticEmployees";

const formSchema = z.object({
	name: z.string().min(1).max(100),
	description: z.string().min(0).max(1000),
	cohortType: z.enum(["static", "dynamic"]),
	selectionObjectID: z.string().min(1).max(100).nullable(),
	currentPage: z.enum(["setup", "setup-duplicate", "static-employees", "dynamic-employees"]),
	color: z.string().startsWith("#").min(0).max(7),
	query: z.optional(z.object({})),
});

export type FormState = z.infer<typeof formSchema>;

const formatDuplicateName = (name: string) => {
	const copyAppend = " (Copy)";
	const charactersOverMaxLength = name.length + copyAppend.length - MAX_COHORT_NAME_LENGTH;
	if (charactersOverMaxLength > 0) {
		name = name.substring(0, name.length - charactersOverMaxLength);
	}
	return `${name}${copyAppend}`;
};

export interface FormStateProps {
	state: FormState;
	validate: () => ValidationResult;
	updateState: (newValues: Partial<FormState>) => void;
	isDuplication?: boolean;
	initialUserSelectionID?: string;
}

const usePageTitle = (state: FormState) => {
	switch (state.currentPage) {
		case "setup":
			return (
				<div className={css.header}>
					<Text size="paragraph-1" weight="semi-bold" color="neutral-9" className={css.title}>
						Create A Cohort
					</Text>
				</div>
			);
		case "setup-duplicate":
			return (
				<div className={css.header}>
					<Text size="paragraph-1" weight="semi-bold" color="neutral-9" className={css.title}>
						Duplicate A Cohort
					</Text>
				</div>
			);
		case "static-employees":
			return (
				<div className={css.header}>
					<Text size="paragraph-1" weight="semi-bold" color="neutral-9">
						<span className={css.title}>
							Add Employees to {state.name}
							<div className={css.infoIcon}>
								<Tooltip
									placement="right"
									message="Analytics dashboard charts will only display results for a cohort if a sufficient number of cohort members (3) have responded to surveys."
								>
									<FontAwesomeIcon icon={faCircleInfo} />
								</Tooltip>
							</div>
						</span>
					</Text>
				</div>
			);
		case "dynamic-employees":
			return (
				<div className={css.header}>
					<Text size="paragraph-1" weight="semi-bold" color="neutral-9">
						<span className={`${css.title} ${css.cohortTitle}`}>
							Dynamic Cohorts populate lists of employees who meet the selected criteria.
						</span>
					</Text>
				</div>
			);
	}
};

const CreateCohortTab = () => {
	const { cohortID } = useParams<{ cohortID: string }>();
	const isDuplication = typeof cohortID !== "undefined";
	const { data, isLoading } = isDuplication ? useCohort(cohortID) : { data: {}, isLoading: false };
	const cohort = data?.cohort;

	const { state, updateForm, validate, setInitialState } = useForm({
		schema: formSchema,
		disableReactWarning: true,
	});

	const pageTitle = usePageTitle(state);
	const initialUserSelectionFromParent = useRef(null);

	const createSelectionObject = async () => {
		let userSelectionId: string | null = null;
		if (isDuplication) {
			const request = await createUserSelectionFromCohort(cohortID);
			userSelectionId = request?.user_selection || null;
		} else {
			const request = await fetchNewSelectionObject();
			userSelectionId = request.user_selection_id;
		}

		updateForm({
			selectionObjectID: userSelectionId,
		});
	};

	useEffect(() => {
		setInitialState({
			name: "",
			description: "",
			cohortType: "static",
			selectionObjectID: null,
			currentPage: isDuplication ? "setup-duplicate" : "setup",
			color: getRandomTopShelfColor(),
		});
		createSelectionObject();

		return () => {
			initialUserSelectionFromParent.current = null;
		};
	}, []);

	useEffect(() => {
		/*  If we are duplicating a cohort, we need to initialize the form with the cohort's name and type
			and create a new selection object for the new cohort
			this occurs after the component mounts and finishes the initial useEffectOnce and loading the old cohorts data
		*/
		const initializeDuplicateCohort = async () => {
			if (!isLoading && cohort) {
				updateForm({
					name: formatDuplicateName(cohort.name),
					description: "",
					cohortType: cohort.type,
					color: getRandomTopShelfColor(),
					query: cohort.type === "dynamic" ? cohort?.query : undefined,
				});
				let user_selection;
				const getUserSelectionObject = isDuplication ? await createUserSelectionFromCohort(cohortID) : null;
				if (getUserSelectionObject) {
					({ user_selection } = getUserSelectionObject);
					initialUserSelectionFromParent.current = user_selection;
				}
			}
		};

		initializeDuplicateCohort();
	}, [isLoading]);

	return (
		<>
			<PageTitle title={isDuplication ? "Duplicate A Cohort" : "Create a Cohort"} />
			<AdminTitle includeSpacer={false}>Employee Cohorts</AdminTitle>
			<Spacer height={4} />

			{pageTitle}

			<Spacer height={7} />
			{isLoading && isDuplication ? (
				<ComponentLoader />
			) : (
				<div>
					{state.currentPage === "setup" || state.currentPage === "setup-duplicate" ? (
						<SetupForm
							state={state}
							updateState={updateForm}
							validate={validate}
							isDuplication={isDuplication}
						/>
					) : null}
					{state.currentPage === "static-employees" ? <StaticCohortEmployees state={state} /> : null}
					{state.currentPage === "dynamic-employees" ? (
						<DynamicEmployeesCreate
							state={state}
							goBack={() => updateForm({ currentPage: isDuplication ? "setup-duplicate" : "setup" })}
						/>
					) : null}
				</div>
			)}
		</>
	);
};

export default CreateCohortTab;
